import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Building2, ChevronDown, FileSignature, FileText, IdCard, Mail, Monitor, PenLine, ScanFace, Search, UserCheck, UserRound } from 'lucide-react';
import { SelectionActionBar, SelectionCheckbox, useTableSelection } from '../components/TableSelection';
import { getBucketStatus, getListStatus, matchesBucket } from '../lib/statusModel';
import { lastUpdatedTimestamp } from '../lib/timestamps';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'action_required', label: 'Action required' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

const statusStyles = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  signed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  needs_review: 'bg-amber-50 text-amber-800 border-amber-300',
  pending: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  draft: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  created: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  ready_to_sign: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  not_received: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  action_required: 'bg-amber-50 text-amber-800 border-amber-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
  failed: 'bg-red-50 text-red-700 border-red-300',
  revoked: 'bg-red-50 text-red-700 border-red-300',
  expired: 'bg-red-50 text-red-700 border-red-300',
};

const statusLabels = {
  needs_review: 'action required',
  action_required: 'action required',
};

function StatusBadge({ status }) {
  const value = status || 'pending';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border ${statusStyles[value] || statusStyles.pending}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {statusLabels[value] || value.replaceAll('_', ' ')}
    </span>
  );
}

function BadgeSequence({ children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <span className="inline-flex items-center">
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && <span aria-hidden="true" className="h-px w-2 bg-zinc-200"/>}
          {child}
        </React.Fragment>
      ))}
    </span>
  );
}

function MethodIcon({ method }) {
  const value = String(method || '').toUpperCase();
  if (value === 'KYC') {
    return <UserCheck className="w-3 h-3" strokeWidth={1.8}/>;
  }
  if (value === 'IDV') {
    return <IdCard className="w-3 h-3" strokeWidth={1.8}/>;
  }
  if (value === 'AES' || value === 'SES') {
    return <FileSignature className="w-3 h-3" strokeWidth={1.8}/>;
  }
  if (value === 'KYB') {
    return <Building2 className="w-3 h-3" strokeWidth={1.8}/>;
  }
  return null;
}

function MethodBadge({ method, ageRequirement = null }) {
  const value = String(method || '').toUpperCase();
  const ageLabel = ageRequirementLabel(ageRequirement);
  return (
    <span className="inline-flex flex-wrap items-center">
      <BadgeSequence>
      <span className="inline-flex h-6 items-center gap-1.5 rounded-full bg-white px-2 py-0.5 uppercase tracking-widest text-[10px] font-mono border text-zinc-500 border-zinc-200">
        <MethodIcon method={method}/>
        {method}
      </span>
      {(value === 'KYC' || value === 'IDV') && ageLabel && <AgeRequirementBadge value={ageRequirement}/>}
      </BadgeSequence>
    </span>
  );
}

function WorkflowStepBadge({ icon: Icon, title, variant = 'default' }) {
  const variantClass = variant === 'external'
    ? 'rounded-lg border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
    : variant === 'linked'
      ? 'rounded-full border-dashed border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500'
      : 'rounded-full border-zinc-200 bg-white text-zinc-400 hover:text-zinc-500';
  return (
    <span className={`inline-flex h-6 min-w-6 items-center justify-center border px-1.5 ${variantClass}`} title={title} aria-label={title}>
      <Icon className="h-3 w-3" strokeWidth={1.8}/>
    </span>
  );
}

function ageRequirementLabel(value) {
  const age = Number(value);
  return [16, 18, 21].includes(age) ? `${age}+` : '';
}

function AgeRequirementBadge({ value }) {
  const label = ageRequirementLabel(value);
  if (!label) return null;
  return (
    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-zinc-200 bg-white px-1.5 text-[10px] font-mono tracking-widest text-zinc-500" title={`Age requirement: ${label}`} aria-label={`Age requirement: ${label}`}>
      {label}
    </span>
  );
}

function WorkflowBadges({ method, linkedToDocument = false, deviceIpAnalysisEnabled = false }) {
  const value = String(method || '').toUpperCase();
  if (value === 'AES') {
    return (
      <BadgeSequence>
        <WorkflowStepBadge icon={UserCheck} title="KYC" variant="linked"/>
        <WorkflowStepBadge icon={PenLine} title="Signature"/>
      </BadgeSequence>
    );
  }
  if (value === 'SES') {
    return (
      <BadgeSequence>
        <WorkflowStepBadge icon={Mail} title="Email signing"/>
        <WorkflowStepBadge icon={PenLine} title="Signature"/>
      </BadgeSequence>
    );
  }
  if (value === 'KYC') {
    return (
      <BadgeSequence>
        {deviceIpAnalysisEnabled && <WorkflowStepBadge icon={Monitor} title="Device and IP analysis"/>}
        <WorkflowStepBadge icon={IdCard} title="ID check"/>
        <WorkflowStepBadge icon={ScanFace} title="Selfie check"/>
        {linkedToDocument && <WorkflowStepBadge icon={FileSignature} title="Document signing" variant="external"/>}
      </BadgeSequence>
    );
  }
  if (value === 'IDV') {
    return (
      <BadgeSequence>
        {deviceIpAnalysisEnabled && <WorkflowStepBadge icon={Monitor} title="Device and IP analysis"/>}
        <WorkflowStepBadge icon={IdCard} title="ID check"/>
        {linkedToDocument && <WorkflowStepBadge icon={FileSignature} title="Document signing" variant="external"/>}
      </BadgeSequence>
    );
  }
  return null;
}

function displayDocumentStatus(item) {
  return getListStatus(item, 'document');
}

function displayVerificationStatus(item) {
  return getBucketStatus(item, 'verification') === 'pending' ? 'pending' : item.status || 'pending';
}

function activityKind(item) {
  return item.kind === 'signature' ? 'document' : 'verification';
}

function activityBucket(item) {
  return item.bucketStatus || getBucketStatus(item, activityKind(item));
}

function matchesFilter(item, filter) {
  if (!filter || filter === 'all') return true;
  if (filter === 'needs_review' || filter === 'action_required') return activityBucket(item) === 'action_required';
  if (['completed', 'pending', 'failed'].includes(filter)) return activityBucket(item) === filter;
  return matchesBucket(item, filter, activityKind(item));
}

function fmtDate(value) {
  return value ? new Date(value).toLocaleString() : '';
}

function formatDocumentType(value) {
  if (!value) return '';
  const labels = {
    id_card: 'ID card',
    passport: 'Passport',
    residence_permit: 'Residence permit',
  };
  const normalized = String(value).trim().toLowerCase();
  return labels[normalized] || normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function methodForVerification(item) {
  const method = String(item?.verification_method || item?.method || '').toLowerCase();
  const idOnly = method === 'idv' || method === 'id_only' || item?.requires_selfie === false;
  return idOnly ? 'IDV' : 'KYC';
}

function referenceCell(value, label, monospace = true) {
  return {
    value: value || '—',
    label: value ? label : '',
    monospace,
  };
}

function missingIdentifierCell() {
  return {
    value: 'Not received',
    label: 'Unknown',
    monospace: true,
    missing: true,
  };
}

function onboardingIdentifierCell() {
  return {
    value: 'Onboarding identity',
    label: '',
    monospace: true,
  };
}

function activitySelectionId(item) {
  return `${item.kind}-${item.id}`;
}

export default function OperationalDashboard() {
  const navigate = useNavigate();
  const [verifications, setVerifications] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get('/verification-sessions'),
      api.get('/signatures'),
    ])
      .then(([verificationRes, signatureRes]) => {
        if (cancelled) return;
        setVerifications(verificationRes.data.items || []);
        setSignatures(signatureRes.data.items || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const activity = useMemo(() => {
    const signaturesById = new Map();
    const signaturesByVerificationId = new Map();
    signatures.forEach((signature) => {
      if (signature?.id) signaturesById.set(signature.id, signature);
      if (signature?.verification_session_id) signaturesByVerificationId.set(signature.verification_session_id, signature);
    });
    const verificationRows = verifications.map((item) => {
      const isOnboarding = item.purpose === 'onboarding_identity' && item.is_platform_verification;
      const claimedName = [item.claimed_first_name, item.claimed_last_name].filter(Boolean).join(' ');
      const name = isOnboarding
        ? (item.extracted_name || claimedName || item.applicant_email || 'Unnamed applicant')
        : (claimedName || item.applicant_email || 'Unnamed applicant');
      const documentMeta = formatDocumentType(item.extracted_document_type);
      const status = displayVerificationStatus(item);
      const bucketStatus = getBucketStatus(item, 'verification');
      const linkedSignature = signaturesById.get(item.linked_signature_id) || signaturesByVerificationId.get(item.id);
      return {
        id: item.id,
        kind: 'verification',
        Icon: UserRound,
        name,
        identifier: item.extracted_document_number
          ? referenceCell(item.extracted_document_number, documentMeta || 'ID card')
          : isOnboarding
            ? onboardingIdentifierCell()
          : missingIdentifierCell(),
        method: methodForVerification(item),
        search: [name, item.applicant_email, item.extracted_name, item.extracted_document_number, documentMeta, item.status].join(' '),
        status,
        bucketStatus,
        rawStatus: item.status,
        linkedToDocument: !!(item.linked_signature_id || linkedSignature),
        linkedToBusiness: !!(item.linked_business_verification_id || item.linked_representative_id),
        linkedToOnboarding: isOnboarding,
        ageRequirement: item.min_age,
        deviceIpAnalysisEnabled: !!item.device_ip_analysis_enabled,
        usesKyc: false,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        lastUpdatedAt: lastUpdatedTimestamp(item),
        expiresAt: item.expires_at,
        route: `/dashboard/verifications/${item.id}`,
      };
    });
    const signatureRows = signatures.map((item) => {
      const signerName = [item.signer_first_name, item.signer_last_name].filter(Boolean).join(' ')
        || item.signer_name
        || item.signer_email
        || 'Unnamed signer';
      const documentMetadata = item.has_document ? 'PDF doc' : 'Generated PDF';
      const status = displayDocumentStatus(item);
      const bucketStatus = getBucketStatus(item, 'document');
      return {
        id: item.id,
        kind: 'signature',
        Icon: FileText,
        name: item.title || 'Untitled document',
        identifier: referenceCell(signerName, 'Signer', false),
        method: item.auth_method === 'kyc' || item.signature_level === 'AES' ? 'AES' : 'SES',
        search: [item.title, documentMetadata, signerName, item.signer_email, item.status, item.signature_level].join(' '),
        status,
        bucketStatus,
        rawStatus: item.status,
        usesKyc: item.auth_method === 'kyc' || item.signature_level === 'AES',
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        lastUpdatedAt: lastUpdatedTimestamp(item),
        expiresAt: item.expires_at,
        route: `/dashboard/signatures/${item.id}`,
      };
    });
    return [...verificationRows, ...signatureRows]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [signatures, verifications]);

  const stats = useMemo(() => {
    return activity.reduce((acc, item) => {
      const bucket = activityBucket(item);
      if (bucket === 'completed') acc.completed++;
      else if (bucket === 'action_required') acc.actionRequired++;
      else if (bucket === 'pending') acc.pending++;
      else if (bucket === 'failed') acc.failed++;
      return acc;
    }, { completed: 0, actionRequired: 0, pending: 0, failed: 0 });
  }, [activity]);

  const filteredActivity = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return activity.filter((item) => {
      if (!matchesFilter(item, filter)) return false;
      if (!needle) return true;
      return item.search.toLocaleLowerCase().includes(needle);
    });
  }, [activity, filter, query]);
  const tableSelection = useTableSelection(filteredActivity, activitySelectionId);
  const selectedFilterLabel = FILTERS.find((item) => item.value === filter)?.label || 'All';
  const filterButtonLabel = filter === 'all' ? 'Status' : `Status: ${selectedFilterLabel}`;

  return (
    <div data-testid="operational-dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Workspace</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-zinc-600 mt-2">Overview of KYC verification and signing activity across your organization.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { k: 'completed', label: 'Completed', v: stats.completed },
          { k: 'action_required', label: 'Action required', v: stats.actionRequired },
          { k: 'pending', label: 'Pending', v: stats.pending },
          { k: 'failed', label: 'Failed', v: stats.failed },
        ].map((card) => (
          <div key={card.k} className="border border-zinc-200 rounded-sm bg-white p-4" data-testid={`dashboard-stat-${card.k}`}>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{card.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{card.v}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
        <div
          className="relative"
          data-testid="dashboard-activity-filter"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setFilterMenuOpen(false);
            }
          }}
        >
          <button
            type="button"
            onClick={() => setFilterMenuOpen((open) => !open)}
            className="h-[34px] inline-flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500 hover:border-zinc-300"
            aria-haspopup="listbox"
            aria-expanded={filterMenuOpen}
          >
            {filterButtonLabel}
            <ChevronDown className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5}/>
          </button>
          {filterMenuOpen && (
            <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-44 border border-zinc-200 rounded-sm bg-white py-1">
              {FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    setFilter(item.value);
                    setFilterMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-[10px] font-medium uppercase tracking-widest ${filter === item.value ? 'text-zinc-500' : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50'}`}
                  role="option"
                  aria-selected={filter === item.value}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="w-full sm:w-80 h-[34px] flex items-center gap-2 border border-zinc-200 rounded-sm bg-white px-3">
          <Search className="w-4 h-4 text-zinc-400" strokeWidth={1.5}/>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className="flex-1 h-full text-[10px] font-medium uppercase tracking-widest bg-transparent text-zinc-500 focus:outline-none placeholder:text-zinc-400"
            placeholder="Search activity"
          />
        </div>
      </div>

      <SelectionActionBar count={tableSelection.selectedCount} onClear={tableSelection.clearSelection}/>
      <section className="border border-zinc-200 bg-white overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-xs uppercase tracking-widest text-zinc-500 text-left">
              <th className="w-10 px-4 py-3 align-middle font-medium">
                <SelectionCheckbox
                  checked={tableSelection.allVisibleSelected}
                  indeterminate={tableSelection.someVisibleSelected && !tableSelection.allVisibleSelected}
                  onCheckedChange={tableSelection.toggleAllVisible}
                  ariaLabel="Select all visible activity rows"
                />
              </th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Identifier</th>
              <th className="px-4 py-3 font-medium">Method</th>
              <th className="px-4 py-3 font-medium">Workflow</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">Loading...</td></tr>
            ) : filteredActivity.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-sm text-zinc-500">No activity found.</td></tr>
            ) : filteredActivity.map((item) => {
              const rowId = activitySelectionId(item);
              const isSelected = tableSelection.isSelected(rowId);
              return (
              <tr key={rowId} onClick={() => navigate(item.route)}
                className={`border-b border-zinc-100 cursor-pointer ${isSelected ? 'bg-zinc-50/80' : 'hover:bg-zinc-50/60'}`}>
                <td className="w-10 px-4 py-4 align-middle">
                  <SelectionCheckbox
                    checked={isSelected}
                    onCheckedChange={() => tableSelection.toggleRow(rowId)}
                    ariaLabel={`Select ${item.name}`}
                  />
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="flex items-start gap-2 min-w-0">
                    <item.Icon className="w-4 h-4 mt-0.5 text-zinc-400 shrink-0" strokeWidth={1.5}/>
                    <div className="min-w-0">
                      <div className="font-medium truncate max-w-[12rem]" title={item.name}>{item.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 align-middle">
                  <div className="min-w-0">
                    <div className={`${item.identifier.monospace ? 'font-mono text-xs text-zinc-500' : 'text-sm leading-tight text-zinc-700'} truncate`}>
                      {item.identifier.value}
                    </div>
                    {item.identifier.label && <div className={`${item.identifier.monospace ? 'mt-1 leading-4' : 'mt-0.5 leading-tight'} text-xs text-zinc-500`}>{item.identifier.label}</div>}
                  </div>
                </td>
                <td className="px-4 py-4 align-middle">
                  <MethodBadge method={item.method} ageRequirement={item.ageRequirement}/>
                </td>
                <td className="px-4 py-4 align-middle">
                  <WorkflowBadges method={item.method} linkedToDocument={item.linkedToDocument} deviceIpAnalysisEnabled={item.deviceIpAnalysisEnabled}/>
                </td>
                <td className="px-4 py-4 align-middle"><StatusBadge status={item.status}/></td>
                <td className="px-4 py-4 align-middle font-mono text-xs text-zinc-500">{fmtDate(item.lastUpdatedAt)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
